// Admin "Locations" panel: configure who may see which office location's
// numbers in the Numbers tab. Mirrors the call-flow viewer-group model — a
// logical location maps one or more raw Entra `officeLocation` strings to the
// Entra security groups whose members may view those numbers. Everyone always
// sees their OWN office location implicitly, and admins see all; this panel only
// grants the extra cross-location visibility. Fetches its own data so the poll
// only runs while the tab is mounted (the render site gates on view/tab/admin).

import { useState, useEffect } from 'react';
import { MapPin, Plus, Trash2, X, Search, Send, AlertCircle, UsersRound, Headphones, EyeOff } from 'lucide-react';
import { api, Group, NumbersLocationAccess, NumbersLocationAccessInput, NumbersHidden } from '../api/client';
import { useApi } from '../hooks/useApi';

interface QueueOption { id: string; displayName: string; }

interface NumbersAccessAdminProps {
  groups: Group[];
  /** Call queues from the directory, offered for assignment to a location. */
  queues: QueueOption[];
  officeLocationSuggestions: string[];
  /** Distinct Entra departments, suggested for the hide-by-department rule. */
  departmentSuggestions: string[];
  /** The tenant-wide hidden config (rules + individually hidden entries). */
  hidden: NumbersHidden | null;
  onSaveHidden: (next: NumbersHidden) => Promise<void>;
  accentColor: string;
  onToast: (message: string, type?: 'success' | 'error') => void;
}

function slugify(name: string): string {
  return name.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function NumbersAccessAdmin({ groups, queues, officeLocationSuggestions, departmentSuggestions, hidden, onSaveHidden, accentColor, onToast }: NumbersAccessAdminProps) {
  const { data, loading, error, refetch } = useApi(() => api.listNumbersAccess(), { pollMs: 30000 });
  const [editing, setEditing] = useState<NumbersLocationAccess | { isNew: true } | null>(null);

  const mappings = data || [];

  async function save(id: string, body: NumbersLocationAccessInput) {
    try {
      await api.putNumbersAccess(id, body);
      await refetch();
      setEditing(null);
      onToast('Location access saved');
    } catch (e: any) { onToast(e.message || 'Save failed', 'error'); }
  }

  async function remove(id: string) {
    try {
      await api.deleteNumbersAccess(id);
      await refetch();
      setEditing(null);
      onToast('Location access deleted');
    } catch (e: any) { onToast(e.message || 'Delete failed', 'error'); }
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-4">
      <HiddenRulesPanel
        hidden={hidden}
        officeLocationSuggestions={officeLocationSuggestions}
        departmentSuggestions={departmentSuggestions}
        accentColor={accentColor}
        onSave={onSaveHidden}
      />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-1">Location access</h2>
            <p className="text-xs text-gray-500 max-w-2xl">
              Control who sees numbers in the <span className="font-medium">Numbers</span> tab. Map a location to one or
              more Entra security groups — members of those groups see that location's numbers. Everyone always sees
              their own office location, and admins see every location.
            </p>
          </div>
          <button onClick={() => setEditing({ isNew: true })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded font-medium shrink-0"
            style={{ backgroundColor: accentColor }}>
            <Plus className="w-4 h-4" />Add location
          </button>
        </div>

        {loading && mappings.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : error && mappings.length === 0 ? (
          <div className="m-5 border-2 border-dashed border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-7 h-7 text-red-300 mx-auto mb-1.5" />
            <div className="text-sm text-gray-700">Couldn't load location access</div>
            <div className="text-xs text-gray-400">{error.message}</div>
          </div>
        ) : mappings.length === 0 ? (
          <div className="m-5 border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
            <MapPin className="w-7 h-7 text-gray-300 mx-auto mb-1.5" />
            <div className="text-sm text-gray-500 mb-1">No location access configured</div>
            <div className="text-xs text-gray-400">
              Users currently see only their own office location. Add a location to grant cross-location visibility.
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {mappings.map(m => (
              <div key={m.id} className="px-5 py-3 flex items-center gap-3 group hover:bg-gray-50">
                <div className="w-9 h-9 rounded bg-gray-100 flex items-center justify-center text-gray-500 shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{m.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {m.officeLocations.join(', ')}
                    <span className="text-gray-400"> · {m.matchMode === 'prefix' ? 'starts with' : 'exact'}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 shrink-0 hidden sm:flex items-center gap-3">
                  <span className="flex items-center gap-1"><UsersRound className="w-3.5 h-3.5 text-gray-400" />{m.viewerGroupIds.length} group{m.viewerGroupIds.length === 1 ? '' : 's'}</span>
                  <span className="flex items-center gap-1"><Headphones className="w-3.5 h-3.5 text-gray-400" />{m.queueIds?.length || 0} queue{(m.queueIds?.length || 0) === 1 ? '' : 's'}</span>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setEditing(m)} className="px-2.5 py-1 text-xs border border-gray-300 rounded hover:bg-white">Edit</button>
                  <button onClick={() => remove(m.id)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <AccessEditor
          initial={'isNew' in editing ? null : editing}
          existingIds={mappings.map(m => m.id)}
          groups={groups}
          queues={queues}
          officeLocationSuggestions={officeLocationSuggestions}
          accentColor={accentColor}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

interface AccessEditorProps {
  initial: NumbersLocationAccess | null;
  existingIds: string[];
  groups: Group[];
  queues: QueueOption[];
  officeLocationSuggestions: string[];
  accentColor: string;
  onSave: (id: string, body: NumbersLocationAccessInput) => Promise<void>;
  onCancel: () => void;
}

function AccessEditor({ initial, existingIds, groups, queues, officeLocationSuggestions, accentColor, onSave, onCancel }: AccessEditorProps) {
  const [name, setName] = useState(initial?.name || '');
  const [officeLocations, setOfficeLocations] = useState<string[]>(initial?.officeLocations || []);
  const [matchMode, setMatchMode] = useState<'exact' | 'prefix'>(initial?.matchMode || 'exact');
  const [viewerGroupIds, setViewerGroupIds] = useState<string[]>((initial?.viewerGroupIds || []).map(g => g.toLowerCase()));
  const [queueIds, setQueueIds] = useState<string[]>((initial?.queueIds || []).map(q => q.toLowerCase()));
  const [locInput, setLocInput] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [queueSearch, setQueueSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const isNew = !initial;
  const id = initial ? initial.id : slugify(name);

  function addLoc(v: string) {
    const t = v.trim();
    if (!t) return;
    if (!officeLocations.some(x => x.toLowerCase() === t.toLowerCase())) {
      setOfficeLocations([...officeLocations, t]);
    }
    setLocInput('');
  }
  function removeLoc(v: string) { setOfficeLocations(officeLocations.filter(x => x !== v)); }
  function toggleGroup(gid: string) {
    const lid = gid.toLowerCase();
    setViewerGroupIds(viewerGroupIds.includes(lid) ? viewerGroupIds.filter(x => x !== lid) : [...viewerGroupIds, lid]);
  }
  function toggleQueue(qid: string) {
    const lid = qid.toLowerCase();
    setQueueIds(queueIds.includes(lid) ? queueIds.filter(x => x !== lid) : [...queueIds, lid]);
  }

  const filteredGroups = groups.filter(g => !groupSearch || g.displayName.toLowerCase().includes(groupSearch.toLowerCase()));
  const filteredQueues = queues.filter(q => !queueSearch || q.displayName.toLowerCase().includes(queueSearch.toLowerCase()));
  const availableSuggestions = officeLocationSuggestions.filter(s => !officeLocations.some(x => x.toLowerCase() === s.toLowerCase()));

  const idCollision = isNew && existingIds.includes(id);
  const canSave = !!name.trim() && officeLocations.length > 0 && !!id && !idCollision && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(id, { name: name.trim(), officeLocations, matchMode, viewerGroupIds, queueIds });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold">{isNew ? 'Add location access' : `Edit ${initial!.name}`}</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Location name *</label>
            <input type="text" autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bocholt"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
            {idCollision && <p className="text-[11px] text-red-600 mt-1">A location with this name already exists.</p>}
          </div>

          {/* Office locations */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Office locations (Entra) *</label>
            <p className="text-[11px] text-gray-400 mb-1.5">The raw Entra "Office location" values this location covers. Add each variant you use.</p>
            <div className="flex gap-1.5">
              <input type="text" value={locInput} list="office-loc-suggestions"
                onChange={e => setLocInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLoc(locInput); } }}
                placeholder="Type or pick a value, then Add"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
              <button onClick={() => addLoc(locInput)} disabled={!locInput.trim()}
                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Add</button>
            </div>
            <datalist id="office-loc-suggestions">
              {availableSuggestions.map(s => <option key={s} value={s} />)}
            </datalist>
            {officeLocations.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {officeLocations.map(loc => (
                  <span key={loc} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                    {loc}
                    <button onClick={() => removeLoc(loc)} className="text-gray-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Match mode */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Match</label>
            <div className="flex bg-gray-100 rounded-md p-1 w-fit">
              {(['exact', 'prefix'] as const).map(mode => (
                <button key={mode} onClick={() => setMatchMode(mode)}
                  className={`px-3 py-1.5 text-xs rounded transition-colors ${matchMode === mode ? 'bg-white shadow-sm font-medium' : 'text-gray-600'}`}
                  style={matchMode === mode ? { color: accentColor } : {}}>
                  {mode === 'exact' ? 'Exact match' : 'Starts with'}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              {matchMode === 'exact'
                ? 'Included when a user’s office location exactly equals one of the values above.'
                : 'Included when a user’s office location starts with one of the values above (handles "DE-Bocholt", "Bocholt HQ", …).'}
            </p>
          </div>

          {/* Viewer groups */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Who can view ({viewerGroupIds.length} selected)</label>
            <p className="text-[11px] text-gray-400 mb-1.5">Members of these Entra security groups can see this location's numbers.</p>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input type="text" value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Search groups…"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
            </div>
            <div className="border border-gray-200 rounded max-h-48 overflow-y-auto divide-y divide-gray-100">
              {filteredGroups.length === 0 ? (
                <div className="p-4 text-center text-xs text-gray-400">No groups found.</div>
              ) : filteredGroups.slice(0, 100).map(g => {
                const checked = viewerGroupIds.includes(g.aadGroupId.toLowerCase());
                return (
                  <label key={g.aadGroupId} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={checked} onChange={() => toggleGroup(g.aadGroupId)} className="w-4 h-4 rounded accent-purple-600" />
                    <span className="text-sm text-gray-800 truncate">{g.displayName}</span>
                  </label>
                );
              })}
            </div>
            {viewerGroupIds.length === 0 && (
              <p className="text-[11px] text-amber-600 mt-1">No groups selected — only users whose own office location matches will see these numbers.</p>
            )}
          </div>

          {/* Call queues at this location */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Call queues at this location ({queueIds.length} selected)</label>
            <p className="text-[11px] text-gray-400 mb-1.5">Assigned queues group under this location in the Numbers tab and follow the access above. Queues left unassigned stay visible to everyone.</p>
            {queues.length === 0 ? (
              <div className="text-[11px] text-gray-400">No call queues found in the directory.</div>
            ) : (
              <>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input type="text" value={queueSearch} onChange={e => setQueueSearch(e.target.value)} placeholder="Search call queues…"
                    className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
                </div>
                <div className="border border-gray-200 rounded max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {filteredQueues.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-400">No call queues found.</div>
                  ) : filteredQueues.slice(0, 100).map(q => {
                    const checked = queueIds.includes(q.id.toLowerCase());
                    return (
                      <label key={q.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" checked={checked} onChange={() => toggleQueue(q.id)} className="w-4 h-4 rounded accent-purple-600" />
                        <Headphones className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-800 truncate">{q.displayName}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-white">Cancel</button>
          <button onClick={handleSave} disabled={!canSave}
            className="px-3 py-1.5 text-sm text-white rounded font-medium disabled:opacity-50 flex items-center gap-1.5"
            style={{ backgroundColor: canSave ? accentColor : '#9ca3af' }}>
            <Send className="w-3.5 h-3.5" />{saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Small add/remove chip editor reused for the hide-by-department / -office /
// -keyword rule lists.
function ChipInput({ label, hint, values, onChange, suggestions, listId, placeholder }: {
  label: string; hint?: string; values: string[]; onChange: (v: string[]) => void;
  suggestions?: string[]; listId?: string; placeholder?: string;
}) {
  const [input, setInput] = useState('');
  const add = (v: string) => {
    const t = v.trim();
    if (!t) return;
    if (!values.some(x => x.toLowerCase() === t.toLowerCase())) onChange([...values, t]);
    setInput('');
  };
  const avail = (suggestions || []).filter(sx => !values.some(x => x.toLowerCase() === sx.toLowerCase()));
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {hint && <p className="text-[11px] text-gray-400 mb-1.5">{hint}</p>}
      <div className="flex gap-1.5">
        <input type="text" value={input} list={listId}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
        <button onClick={() => add(input)} disabled={!input.trim()}
          className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Add</button>
      </div>
      {listId && suggestions && <datalist id={listId}>{avail.map(sx => <option key={sx} value={sx} />)}</datalist>}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {values.map(v => (
            <span key={v} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
              {v}
              <button onClick={() => onChange(values.filter(x => x !== v))} className="text-gray-400 hover:text-red-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Tenant-wide hide rules for the Numbers tab. Individual entries are hidden via
// the eye-off button on the Numbers tab; this panel manages the bulk rules.
function HiddenRulesPanel({ hidden, officeLocationSuggestions, departmentSuggestions, accentColor, onSave }: {
  hidden: NumbersHidden | null;
  officeLocationSuggestions: string[];
  departmentSuggestions: string[];
  accentColor: string;
  onSave: (next: NumbersHidden) => Promise<void>;
}) {
  const [hideAllQueues, setHideAllQueues] = useState(false);
  const [hideNoNumber, setHideNoNumber] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);
  const [namePatterns, setNamePatterns] = useState<string[]>([]);
  const [officeLocations, setOfficeLocations] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Resync from server config (loads once + after each save; no polling, so this
  // won't clobber an in-progress edit).
  useEffect(() => {
    setHideAllQueues(!!hidden?.rules.hideAllQueues);
    setHideNoNumber(!!hidden?.rules.hideNoNumber);
    setDepartments(hidden?.rules.departments || []);
    setNamePatterns(hidden?.rules.namePatterns || []);
    setOfficeLocations(hidden?.rules.officeLocations || []);
  }, [hidden]);

  const baseline = {
    hideAllQueues: !!hidden?.rules.hideAllQueues,
    hideNoNumber: !!hidden?.rules.hideNoNumber,
    departments: hidden?.rules.departments || [],
    namePatterns: hidden?.rules.namePatterns || [],
    officeLocations: hidden?.rules.officeLocations || [],
  };
  const current = { hideAllQueues, hideNoNumber, departments, namePatterns, officeLocations };
  const dirty = JSON.stringify(current) !== JSON.stringify(baseline);
  const individualCount = hidden?.hiddenIds.length || 0;

  async function save() {
    setSaving(true);
    try {
      await onSave({
        hiddenIds: hidden?.hiddenIds || [],
        exceptionIds: hidden?.exceptionIds || [],
        rules: { ...current },
        siteHiddenIds: hidden?.siteHiddenIds || {},
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
      <div className="p-5 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <EyeOff className="w-4 h-4 text-gray-400" />Hidden numbers &amp; rules
        </h2>
        <p className="text-xs text-gray-500 max-w-2xl">
          Remove entries from the <span className="font-medium">Numbers</span> tab for everyone (hidden entries are never sent to non-admins). Hide individual people or queues with the eye-off button on the Numbers tab; use the rules below to hide groups of entries at once.
          {individualCount > 0 && <> Currently <span className="font-medium">{individualCount}</span> hidden individually.</>}
        </p>
      </div>
      <div className="p-5 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={hideAllQueues} onChange={e => setHideAllQueues(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-purple-600" />
          <span className="text-xs text-gray-700 leading-relaxed"><span className="block font-medium text-gray-900 mb-0.5">Hide all call queues</span>Remove every call queue / auto-attendant from the Numbers tab.</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={hideNoNumber} onChange={e => setHideNoNumber(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-purple-600" />
          <span className="text-xs text-gray-700 leading-relaxed"><span className="block font-medium text-gray-900 mb-0.5">Hide people with no phone number</span>Only show people who have a direct number (call queues are unaffected by this rule).</span>
        </label>
        <ChipInput label="Hide by department" hint="Hide everyone whose Entra department matches (case-insensitive, exact)."
          values={departments} onChange={setDepartments} suggestions={departmentSuggestions} listId="hide-dept-suggestions"
          placeholder="Type or pick a department, then Add" />
        <ChipInput label="Hide by office location" hint="Hide everyone whose raw Entra office location matches (case-insensitive, exact)."
          values={officeLocations} onChange={setOfficeLocations} suggestions={officeLocationSuggestions} listId="hide-loc-suggestions"
          placeholder="Type or pick an office location, then Add" />
        <ChipInput label="Hide by name / sign-in keyword" hint="Hide people or call queues whose display name or sign-in (UPN/email) contains a keyword — e.g. svc, test."
          values={namePatterns} onChange={setNamePatterns}
          placeholder="Type a keyword, then Add" />
      </div>
      <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
        <button onClick={save} disabled={!dirty || saving}
          className="px-3 py-1.5 text-sm text-white rounded font-medium disabled:opacity-50 flex items-center gap-1.5"
          style={{ backgroundColor: dirty && !saving ? accentColor : '#9ca3af' }}>
          <Send className="w-3.5 h-3.5" />{saving ? 'Saving…' : 'Save rules'}
        </button>
      </div>
    </div>
  );
}
